import { utf8ByteLength, sanitizeDiagnosticData } from 'src/features/diagnostics/sanitize';

// Each part gets space inside the existing 8 KiB event budget. Body values are
// readable JSON text so deeply nested API payloads do not exceed wire depth 6.
export const HTTP_DIAGNOSTIC_BODY_READ_LIMIT = 32_768;
export const HTTP_DIAGNOSTIC_BODY_TIMEOUT_MS = 1_000;
const PART_BYTES = 1_600;
const MAX_BODY_READS = 8;
let activeBodyReads = 0;

function boundedText(value: string): { value: string; truncated: boolean } {
  // Count JSON escaping too: quote-heavy bodies must fit the wire budget.
  if (utf8ByteLength(JSON.stringify(value)) <= PART_BYTES) return { value, truncated: false };
  let end = Math.min(value.length, PART_BYTES);
  while (utf8ByteLength(JSON.stringify(`${value.slice(0, end)}…`)) > PART_BYTES) end -= 1;
  return { value: `${value.slice(0, end)}…`, truncated: true };
}

export function captureHttpHeaders(headers: unknown): unknown {
  try {
    const record = headers instanceof Headers ? Object.fromEntries(headers.entries()) : headers;
    const safe = sanitizeDiagnosticData(record ?? {});
    const text = JSON.stringify(safe);
    return utf8ByteLength(text) <= PART_BYTES ? safe : { ...boundedText(text), format: 'json' };
  } catch {
    return { unavailable: 'Headers could not be read' };
  }
}

function formEntries(form: FormData | URLSearchParams): Record<string, unknown> {
  const entries: Record<string, unknown> = Object.create(null);
  let count = 0;
  // Stop iteration at the budget instead of materializing a potentially huge form.
  const iterator = form.entries();
  let next = iterator.next();
  while (!next.done) {
    const [key, value] = next.value;
    if (count >= 100) {
      entries['…'] = '[additional form fields omitted]';
      break;
    }
    count += 1;
    const item =
      typeof value === 'string'
        ? value
        : {
            name: value.name,
            size: value.size,
            contentType: value.type,
            format: 'binary',
          };
    const previous = entries[key];
    entries[key] =
      previous === undefined ? item : [...(Array.isArray(previous) ? previous : [previous]), item];
    next = iterator.next();
  }
  return entries;
}

export function captureHttpBody(body: unknown, contentType = '', truncated = false): unknown {
  try {
    if (body === undefined || body === null) return null;
    let value = body;
    if (typeof body === 'string') {
      truncated ||= utf8ByteLength(body) > HTTP_DIAGNOSTIC_BODY_READ_LIMIT;
      value = body.slice(0, HTTP_DIAGNOSTIC_BODY_READ_LIMIT);
      try {
        value = JSON.parse(value as string);
      } catch {
        if (contentType.includes('application/x-www-form-urlencoded'))
          value = formEntries(new URLSearchParams(value as string));
      }
    } else if (body instanceof FormData || body instanceof URLSearchParams) {
      value = formEntries(body);
    } else if (body instanceof Blob) {
      return { format: 'binary', bytes: body.size, contentType: body.type };
    } else if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
      return { format: 'binary', bytes: body.byteLength };
    } else if (body instanceof ReadableStream) {
      return { unavailable: 'Streaming request body is owned by the transport' };
    }
    const safe = sanitizeDiagnosticData(value);
    const text = typeof safe === 'string' ? safe : JSON.stringify(safe);
    const bounded = boundedText(text ?? '');
    return {
      format: typeof value === 'string' ? 'text' : 'json',
      value: bounded.value,
      ...(truncated || bounded.truncated ? { truncated: true } : {}),
    };
  } catch {
    return { unavailable: 'Body could not be serialized' };
  }
}

/** Read only a clone, on a side promise. Cancel the clone at the byte/time cap;
 * never await tee cancellation, which may wait for the application's reader. */
export async function captureFetchBody(message: Request | Response): Promise<unknown> {
  if (activeBodyReads >= MAX_BODY_READS) return { unavailable: 'Diagnostic body reader limit' };
  activeBodyReads += 1;
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const copy = message.clone();
    if (!copy.body) return null;
    reader = copy.body.getReader();
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error('Diagnostic body read timed out')),
        HTTP_DIAGNOSTIC_BODY_TIMEOUT_MS
      );
    });
    const chunks: Uint8Array[] = [];
    let bytes = 0;
    let truncated = false;
    while (bytes < HTTP_DIAGNOSTIC_BODY_READ_LIMIT) {
      // A stream reader must be advanced sequentially to enforce the byte cap.
      // eslint-disable-next-line no-await-in-loop
      const next = await Promise.race([reader.read(), timeout]);
      if (next.done) break;
      const chunk = next.value.slice(0, HTTP_DIAGNOSTIC_BODY_READ_LIMIT - bytes);
      chunks.push(chunk);
      bytes += chunk.byteLength;
      if (bytes >= HTTP_DIAGNOSTIC_BODY_READ_LIMIT) truncated = true;
    }
    const buffer = new Uint8Array(bytes);
    let offset = 0;
    chunks.forEach((chunk) => {
      buffer.set(chunk, offset);
      offset += chunk.byteLength;
    });
    const contentType = copy.headers.get('content-type') ?? '';
    if (contentType && !/json|text|xml|javascript|x-www-form-urlencoded/i.test(contentType))
      return {
        format: 'binary',
        bytesRead: bytes,
        contentType,
        ...(truncated ? { truncated } : {}),
      };
    return captureHttpBody(new TextDecoder().decode(buffer), contentType, truncated);
  } catch {
    return { unavailable: 'Body copy unavailable or read timed out' };
  } finally {
    if (timer) clearTimeout(timer);
    if (reader) reader.cancel().catch(() => undefined);
    activeBodyReads -= 1;
  }
}
